import { useSyncExternalStore } from "react";
import type { StaffIdentity } from "@rekati/api";
import { storage } from "@rekati/utils";

/*
 * The staff bearer token (docs/frontend-integration.md 6.1), 24 h by default.
 * sessionStorage: a shared frontdesk tablet closes the tab and the session is
 * gone from it; a 401 anywhere clears it too.
 */
export interface StaffSession {
  token: string;
  expiresAt: string;
  staff: StaffIdentity;
}

const KEY = "rekati.staff.session";
const store = storage("session");
const listeners = new Set<() => void>();

function load(): StaffSession | null {
  const s = store.read<StaffSession>(KEY);
  return s?.token && new Date(s.expiresAt).getTime() > Date.now() ? s : null;
}

let current: StaffSession | null = load();
const emit = () => listeners.forEach((l) => l());

export const staffSession = {
  get: () => current,
  token: () => current?.token ?? null,
  set(next: StaffSession) {
    current = next;
    store.write(KEY, next);
    emit();
  },
  /** `GET /v1/staff/me` is the authority on the role; keep the cached copy in line. */
  updateStaff(staff: StaffIdentity) {
    if (!current) return;
    current = { ...current, staff };
    store.write(KEY, current);
    emit();
  },
  clear() {
    if (!current) return;
    current = null;
    store.remove(KEY);
    emit();
  },
  subscribe(l: () => void) {
    listeners.add(l);
    return () => listeners.delete(l);
  },
};

export function useStaffSession(): StaffSession | null {
  return useSyncExternalStore(staffSession.subscribe, staffSession.get, staffSession.get);
}
